import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { applyTheme } from "@/lib/themes";
import { Trash2, Plus, Minus, ShoppingCart, Clock, ArrowRight } from "lucide-react";
import ShopNav from "@/components/ShopNav";

export default function CartPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "demo";
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);

  const load = useCallback(() => api.get(`/shop/cart?token=${token}`).then((r) => setCart(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); api.get(`/employee/me?token=${token}`).then((r) => applyTheme(r.data.theme || "standard")).catch(() => {}); }, [load, token]);

  const update = async (index, patch) => { const r = await api.patch(`/shop/cart/item?token=${token}`, { index, ...patch }); setCart(r.data); };
  const remove = async (index) => { const r = await api.delete(`/shop/cart/item/${index}?token=${token}`); setCart(r.data); toast("Removed"); };

  if (!cart) return <div className="min-h-screen" style={{ background: "var(--bq-bg)" }}><ShopNav token={token} active="cart" /></div>;
  const count = cart.items.reduce((a, i) => a + i.qty, 0);

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} cartCount={count} active="cart" />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <h1 className="text-2xl font-black uppercase mb-4">Your Cart</h1>
        {cart.items.length === 0 ? (
          <div className="brutal-card p-12 text-center" data-testid="cart-empty">
            <ShoppingCart size={48} className="mx-auto mb-3" style={{ color: "var(--bq-muted)" }} />
            <p className="font-black uppercase">Your cart is empty</p>
            <button onClick={() => navigate(`/shop?token=${token}`)} className="brutal-btn mt-4">Continue Shopping</button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
              {cart.items.map((it, realIdx) => <CartRow key={realIdx} it={it} index={cart.items.indexOf(it)} onQty={update} onRemove={remove} />)}
            </div>
            <aside className="brutal-card p-5 h-fit" data-testid="cart-summary">
              <h3 className="font-black uppercase mb-3">Order Summary</h3>
              <Row label="Subtotal" val={`₹${cart.subtotal}`} />
              {cart.redeem_eligible ? <Row label="Redeem available" val={`− ₹${cart.applicable_redeem}`} accent /> : null}
              <div className="border-t-2 border-black my-2" />
              <Row label="Payable" val={`₹${cart.payable_after_redeem}`} big />
              <p className="text-xs mt-1" style={{ color: "var(--bq-muted)" }}>{cart.redeem_eligible ? `You can apply ₹${cart.redeem_value_available} of redeem points at checkout.` : cart.redeem_reason}</p>
              <button data-testid="cart-checkout-btn" onClick={() => navigate(`/shop/checkout?token=${token}`)} className="brutal-btn w-full mt-4">Checkout <ArrowRight size={14} className="inline" /></button>
              <button onClick={() => navigate(`/shop?token=${token}`)} className="brutal-btn-secondary w-full mt-2 text-sm">Continue Shopping</button>
            </aside>
          </div>
        )}

        {cart.saved.length > 0 && (
          <div className="mt-8">
            <h2 className="font-black uppercase mb-3 flex items-center gap-2"><Clock size={18} /> Saved for later</h2>
            <div className="space-y-3">{cart.saved.map((it) => <CartRow key={`s-${cart.items.indexOf(it)}`} it={it} index={cart.items.indexOf(it)} onQty={update} onRemove={remove} saved />)}</div>
          </div>
        )}
      </main>
    </div>
  );
}

function CartRow({ it, index, onQty, onRemove, saved }) {
  const p = it.product;
  return (
    <div className="brutal-card p-3 flex gap-3" data-testid={`cart-row-${p.id}`}>
      <img src={p.images?.[0]} alt={p.name} className="w-20 h-20 object-cover border-2 border-black" />
      <div className="flex-1 min-w-0">
        <div className="font-black leading-tight">{p.name}</div>
        <div className="text-xs" style={{ color: "var(--bq-muted)" }}>{[it.color, it.adult_size, it.kids_size].filter(Boolean).join(" · ")}{it.is_kids ? " · Kids" : ""}</div>
        <div className="font-black mt-1">₹{p.price_after_discount} <span className="text-xs font-normal" style={{ color: "var(--bq-muted)" }}>× {it.qty} = ₹{it.line_total}</span></div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {!saved && <>
            <button data-testid={`cart-dec-${p.id}`} onClick={() => onQty(index, { qty: it.qty - 1 })} className="w-7 h-7 border-2 border-black"><Minus size={11} className="mx-auto" /></button>
            <span className="font-black w-6 text-center">{it.qty}</span>
            <button data-testid={`cart-inc-${p.id}`} onClick={() => onQty(index, { qty: it.qty + 1 })} className="w-7 h-7 border-2 border-black"><Plus size={11} className="mx-auto" /></button>
          </>}
          <button data-testid={`cart-save-${p.id}`} onClick={() => onQty(index, { saved_for_later: !saved })} className="text-xs font-black uppercase underline">{saved ? "Move to cart" : "Save for later"}</button>
          <button data-testid={`cart-remove-${p.id}`} onClick={() => onRemove(index)} className="text-xs font-black uppercase inline-flex items-center gap-1" style={{ color: "var(--bq-danger)" }}><Trash2 size={12} /> Remove</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, val, big, accent }) {
  return <div className="flex justify-between items-center py-1"><span className={big ? "font-black uppercase" : "text-sm"} style={{ color: accent ? "green" : "inherit" }}>{label}</span><span className={big ? "text-xl font-black" : "font-bold"} style={{ color: accent ? "green" : "inherit" }}>{val}</span></div>;
}
